import {
  Box,
  Button,
  Grid,
  IconButton,
  Pagination,
  Stack,
  Typography,
} from "@mui/material";
import React, { useCallback, useState } from "react";
import useCommonGetInit from "../../hooks/useCommonGetInit";
import { ADMIN, API_ENDPOINT } from "../../utils/constants";
import BackBtn from "../../widgets/BackBtn";
import CustomCircularP from "../../widgets/CustomCircularP";
import SearchBar from "../../widgets/searchBar";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Avatar,
} from "@mui/material";
import { ScrollTableContainer } from "../../components/visitors/Visitors";
import ExportExhibitorsButton from "./ExportExhibitorsButton";
import AddIcon from "@mui/icons-material/Add";
import { Link, useNavigate } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import SimpleYesNoDialog from "../../widgets/SimpleYesNoDialog";
import { getHeader, toastError } from "../../utils/util";
import axios from "axios";
import { toast } from "react-toastify";

const Exhibitors = () => {
  const [reload, setReload] = useState(false);
  const [page, setPage] = useState(1);
  const [sq, setSq] = useState("");
  const navigate = useNavigate();
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { data, isLoading, isError, error } = useCommonGetInit(
    `${API_ENDPOINT}user/a/get-exhibitors?page=${page}&sq=${sq}&r=${reload}`
  );

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        func.apply(null, args);
      }, delay);
    };
  };

  const handleSearch = (e) => {
    const vl = e.target.value;
    setSq(vl);
  };

  const debouncedHandleSearch = useCallback(debounce(handleSearch, 500), []);

  const pageChange = (e, page) => {
    setPage(page);
  };

  // Helper function to safely get nested values
  const getFieldValue = (field) => {
    return field?.value ?? "-";
  };

  // Helper function to get full contact name
  const getContactName = (user) => {
    const salutation = user.contactSalutation?.value || "";
    const firstName = user.contactFirstName?.value || "";
    const lastName = user.contactLastName?.value || "";
    const fullName = `${salutation} ${firstName} ${lastName}`.trim();
    return fullName || "-";
  };

  // Helper function to get location string
  const getLocation = (user) => {
    const country = user.country?.value?.name || "";
    const state = user.state?.value?.name || "";
    const city = user.city?.value?.name || "";
    const parts = [country, state, city].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "-";
  };

  // Helper function to get floor allocation
  const getFloorAllocation = (user) => {
    const hallNumber = user.hallNumber?.value || "";
    const standNumber = user.standNumber?.value || "";
    if (hallNumber && standNumber) {
      return `${hallNumber} | ${standNumber}`;
    } else if (hallNumber || standNumber) {
      return hallNumber || standNumber;
    }
    return "-";
  };

  // Helper function to get industry sectors
  const getIndustrySectors = (user) => {
    const sectors = user.industrySector?.value;
    if (Array.isArray(sectors) && sectors.length > 0) {
      return sectors.join(", ");
    }
    return "-";
  };

  return (
    <Box>
      <BackBtn />
      <CustomCircularP show={isLoading} />
      <Grid container mb={2} rowSpacing={2}>
        <Grid item md={2} alignContent={"center"}>
          <Stack direction={"row"} gap={2}>
            <Typography variant="h5">Exhibitors</Typography>
            <Typography alignSelf={"center"} variant="body1">
              count: {data?.totalItems || 0}
            </Typography>
          </Stack>
        </Grid>
        <Grid item md={6}>
          <SearchBar
            placeholder={"Search by company name"}
            size="small"
            onChange={debouncedHandleSearch}
          />
        </Grid>
        <Grid item md={4} alignContent={"center"}>
          <Stack direction={"row"} gap={2}>
            <Box flex={1} display={"flex"} gap={1} justifyContent={"end"}>
              <Button
                onClick={() => navigate("/home/update-exhibitor-excel")}
                variant="outlined"
                startIcon={<AddIcon />}
              >
                Update
              </Button>
              <Button
                onClick={() => navigate("/home/add-exhibitor")}
                variant="outlined"
                startIcon={<AddIcon />}
              >
                Add
              </Button>
            </Box>
            <ExportExhibitorsButton />
          </Stack>
        </Grid>
      </Grid>
      <Box>
        <ScrollTableContainer component={Paper}>
          <Table stickyHeader aria-label="scrollable table">
            <TableHead>
              <TableRow>
                <TableCell>Photo</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Contact Person</TableCell>
                <TableCell>Contact Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Fascia</TableCell>
                <TableCell>Country, State, City</TableCell>
                <TableCell>Floor Allocated</TableCell>
                <TableCell>Industry Sector</TableCell>
                <TableCell>Pin</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!data?.data || data.data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    No data available.
                  </TableCell>
                </TableRow>
              ) : (
                data.data.map((user, index) => (
                  <TableRow key={user._id || index}>
                    <TableCell>
                      <Avatar
                        src={user.profile_image || ""}
                        alt={user.contactFirstName?.value || "User"}
                      />
                    </TableCell>
                    <TableCell>
                      {user._id ? (
                        <Link to={`/home/exhibitor/${user._id}`}>
                          {getFieldValue(user.companyName)}
                        </Link>
                      ) : (
                        getFieldValue(user.companyName)
                      )}
                    </TableCell>
                    <TableCell>{getContactName(user)}</TableCell>
                    <TableCell>{getFieldValue(user.contactEmail)}</TableCell>
                    <TableCell>{getFieldValue(user.phone)}</TableCell>
                    <TableCell>{getFieldValue(user.fascia)}</TableCell>
                    <TableCell>{getLocation(user)}</TableCell>
                    <TableCell>{getFloorAllocation(user)}</TableCell>
                    <TableCell>{getIndustrySectors(user)}</TableCell>
                    <TableCell>
                      {getFieldValue(user.pin)}{" "}
                      {user._id && (
                        <Link to={`/home/edit-exhibitor-form/${user._id}`}>
                          <IconButton size="small">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Link>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </ScrollTableContainer>
      </Box>
      <Box display="flex" justifyContent="center" mt={2} mb={2}>
        {data && (
          <Pagination
            page={page}
            onChange={pageChange}
            count={Math.ceil((data.totalItems || 0) / 30)}
          />
        )}
      </Box>
      {data && data?.data?.length > 0 && (
        <Box>
          <SimpleYesNoDialog
            title="Please Confirm!"
            message={`Are you sure you want to delete all exhibitors?`}
            onYes={async () => {
              setDeleting(true);
              setShowDelete(false);
              try {
                const { data } = await axios.delete(
                  `${API_ENDPOINT}admin/delete-all-exhibitors`,
                  getHeader(ADMIN)
                );
                toast.success(data.message);
                setTimeout(() => {
                  document.location.reload();
                }, 400);
              } catch (error) {
                toastError(error);
              } finally {
                setDeleting(false);
              }
            }}
            show={showDelete}
            setShow={setShowDelete}
          />
          <LoadingButton
            startIcon={<DeleteOutlineIcon fontSize="small" />}
            sx={{ mt: 2 }}
            size="small"
            variant="outlined"
            color="error"
            onClick={() => {
              setShowDelete(true);
            }}
            loading={deleting}
          >
            Delete all exhibitors
          </LoadingButton>
        </Box>
      )}
    </Box>
  );
};

export default Exhibitors;
